/**
 * usePdfCanvas.ts
 *
 * Reusable hook that mirrors the PDF.js rendering pipeline used in Landing.tsx:
 *   base64 PDF → Uint8Array → pdf.js document → canvas render
 *
 * Usage:
 *   const { canvasRef, numPages } = usePdfCanvas(base64Pdf, pageIndex);
 *   <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: 'auto' }} />
 *
 * Returns { canvasRef, numPages } where numPages is 0 until the PDF loads, then the real
 * page count from pdfjs. Callers use numPages to drive prev/next navigation.
 * Passing null/undefined for base64Pdf clears the canvas and does nothing.
 *
 * Quality notes:
 *  - Canvas buffer is rendered at window.devicePixelRatio × container width so it
 *    matches physical screen pixels on HiDPI/retina displays at the CURRENT zoom level.
 *  - A matchMedia listener re-renders whenever the browser zoom changes (DPR change),
 *    keeping the preview sharp after Ctrl+/- or pinch-zoom.
 *  - The PDF source is generated by Puppeteer at deviceScaleFactor:2 so the embedded
 *    images are always 2× native resolution — no quality loss in the source.
 */

import { useRef, useEffect, useState, RefObject } from 'react';

export function usePdfCanvas(
  base64Pdf: string | null | undefined,
  pageIndex: number
): { canvasRef: RefObject<HTMLCanvasElement>; numPages: number } {
  const canvasRef     = useRef<HTMLCanvasElement>(null);
  const [numPages, setNumPages] = useState(0);
  const renderTaskRef = useRef<any>(null);
  const pdfDocRef     = useRef<any>(null);
  // Holds the cleanup function for the active DPR matchMedia listener
  const dprCleanupRef = useRef<(() => void) | null>(null);

  // Re-parse the PDF document whenever base64Pdf changes
  useEffect(() => {
    if (!base64Pdf) {
      pdfDocRef.current = null;
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist');
        (pdfjsLib as any).GlobalWorkerOptions.workerSrc =
          `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${(pdfjsLib as any).version}/pdf.worker.min.js`;
        const bytes = Uint8Array.from(atob(base64Pdf), c => c.charCodeAt(0));
        const doc   = await (pdfjsLib as any).getDocument({ data: bytes }).promise;
        if (!cancelled) {
          pdfDocRef.current = doc;
          setNumPages(doc.numPages);
          renderPage(doc, pageIndex);
        }
      } catch (e) {
        console.error('[usePdfCanvas] failed to load PDF', e);
      }
    })();

    return () => {
      cancelled = true;
      // Remove any active DPR listener when PDF changes
      dprCleanupRef.current?.();
      dprCleanupRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base64Pdf]);

  // Re-render whenever pageIndex changes
  useEffect(() => {
    if (!pdfDocRef.current) return;
    renderPage(pdfDocRef.current, pageIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex]);

  function renderPage(doc: any, idx: number) {
    if (!canvasRef.current) return;

    // Remove the previous DPR listener — we'll register a fresh one after render
    dprCleanupRef.current?.();
    dprCleanupRef.current = null;

    (async () => {
      try {
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }
        const page   = await doc.getPage(idx + 1); // pdf.js is 1-indexed
        const canvas = canvasRef.current;
        if (!canvas) return;

        const contW  = canvas.parentElement?.clientWidth ?? 400;

        // Use current DPR so canvas buffer matches physical screen pixels.
        // This is re-evaluated on every call, so Ctrl+/- zoom is handled
        // correctly as long as we re-render on DPR change (see matchMedia below).
        const dpr    = window.devicePixelRatio || 1;
        const baseVp = page.getViewport({ scale: 1 });
        const scale  = (contW / baseVp.width) * dpr;
        const vp     = page.getViewport({ scale });

        canvas.width  = vp.width;   // physical pixels
        canvas.height = vp.height;  // physical pixels
        // CSS width:'100%' / height:'auto' on the element handles display size —
        // the canvas renders at DPR× resolution displayed at 1× CSS size = sharp.
        const ctx  = canvas.getContext('2d')!;
        const task = page.render({ canvasContext: ctx, viewport: vp });
        renderTaskRef.current = task;
        await task.promise;

        // ── Watch for browser zoom changes ──────────────────────────────────
        // matchMedia fires once when devicePixelRatio changes (Ctrl+/- zoom,
        // pinch-zoom, moving window between monitors). We use { once: true }
        // and re-register inside the handler so the chain is always live.
        const registerDprWatcher = () => {
          const currentDpr = window.devicePixelRatio;
          const mql = window.matchMedia(`(resolution: ${currentDpr}dppx)`);
          const onDprChange = () => {
            // Re-render at the new DPR (renderPage also re-registers the watcher)
            if (pdfDocRef.current) renderPage(pdfDocRef.current, idx);
          };
          mql.addEventListener('change', onDprChange, { once: true });
          // Return cleanup
          return () => mql.removeEventListener('change', onDprChange);
        };

        dprCleanupRef.current = registerDprWatcher();

      } catch (e: any) {
        if (e?.name !== 'RenderingCancelledException') console.error('[usePdfCanvas] render error', e);
      }
    })();
  }

  return { canvasRef, numPages };
}
